import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '@/components/ui';
import { formatKrw, formatUsd } from '@/lib/format';
import type { PnlCurvePoint } from './accountTypes';
import { usePaperChartColors } from './paperChartColors';
import { usePnlCurve } from './useAccount';

/**
 * 실계좌 누적 손익 곡선. (2026-10-16, docs/impl/150 7번)
 *
 * 선 = 그날까지 판 것의 순손익 + 그날 들고 있는 것의 평가손익. 원화 · 달러를 따로 그린다(한 축에 두 통화를 섞지 않는다).
 *
 * 🔴 총자산 곡선이 아니다 — 토스가 입출금 기록을 주지 않아, 총자산으로 그리면 입금한 날이 수익처럼 보인다.
 * 🔴 모의투자 비교 탭에 넣지 않았다(BACKLOG I-13) — 비교 탭은 2,000만 시작 수익률로 견주는데 실계좌는 시작 금액이 없다.
 * dataviz 규칙 — 계열 하나라 범례 없음 · 2px 선 · 세로 안내선 + 툴팁(실현 · 평가 · 합). 색은 환율 차트처럼 --paper-safe 를 빌린다.
 */
export function PnlCurveCard() {
  const { data, error } = usePnlCurve();

  if (error) {
    return <p className="text-xs text-warn">누적 손익을 불러오지 못했습니다. {error.message}</p>;
  }
  if (!data) {
    return null;
  }

  const currencies = [...new Set(data.points.map((p) => p.currency))];
  return (
    <Card title="실계좌 누적 손익">
      {currencies.length === 0 ? (
        <p className="text-sm text-fg-muted">
          아직 쌓인 날이 없습니다. 종목을 들고 있거나 다 판 날(동기화가 보유 기록을 남긴 날)마다 한 점씩 생깁니다.
        </p>
      ) : (
        <div className="space-y-4">
          {currencies.map((currency) => (
            <CurrencyCurve key={currency} currency={currency} points={data.points.filter((p) => p.currency === currency)} />
          ))}
        </div>
      )}
      <p className="mt-2 text-xs text-fg-subtle">
        총자산 곡선이 아닙니다 — 처음부터 판 것의 순손익(수수료 · 세금 뺀 값) + 그날 들고 있는 것의 평가손익(지금 팔 때 세금 · 수수료를 뺀 값이라 위 보유 표의 평가손익보다 조금 작습니다)입니다. 입금 · 출금은 들어가지 않고, 매매일지의 기간별 실현손익과 달리 처음부터 누적입니다. 달러는 달러 그대로입니다(환율 움직임을 섞지 않습니다).
      </p>
    </Card>
  );
}

function CurrencyCurve({ currency, points }: { currency: string; points: PnlCurvePoint[] }) {
  const c = usePaperChartColors();
  const money = (v: number) => (currency === 'USD' ? formatUsd(v) : formatKrw(v));
  const last = points.at(-1);

  return (
    <div>
      <p className="mb-1 text-xs text-fg-muted">
        {currency === 'USD' ? '달러' : '원화'}
        {last && ` · 지금 ${money(last.total)}(실현 ${money(last.realized)} · 평가 ${money(last.unrealized)})`}
      </p>
      <div className="h-48 w-full sm:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.line} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(d: string) => d.slice(5)}
              tick={{ fill: c.axis, fontSize: 11 }}
              stroke={c.line}
              minTickGap={24}
            />
            <YAxis
              tick={{ fill: c.axis, fontSize: 11 }}
              stroke={c.line}
              width={56}
              tickFormatter={(v: number) => v.toLocaleString('ko-KR')}
            />
            <Tooltip
              cursor={{ stroke: c.axis, strokeDasharray: '3 3' }}
              labelFormatter={(d) => String(d)}
              formatter={(_value, _name, item) => {
                const p = item.payload as PnlCurvePoint;
                return [`${money(p.total)} (실현 ${money(p.realized)} · 평가 ${money(p.unrealized)})`, '누적 손익'];
              }}
              contentStyle={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 8, fontSize: 12 }}
            />
            <Line
              dataKey="total"
              stroke={c.safe}
              strokeWidth={2}
              dot={points.length < 3}
              activeDot={{ r: 4, stroke: c.surface, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
